/**
 * Web player data layer.
 *
 * Mirrors the Android `GameRepository`: Firestore is read-only from here apart
 * from a player's own cosmetic marks, and everything that decides an outcome
 * goes through a callable, because the security rules deny clients write access
 * to all of it.
 */

import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';

import { auth, callable, db } from './firebase.js';

export type RewardType =
  | 'EARLY_5'
  | 'EARLY_7'
  | 'CORNERS'
  | 'TOP_ROW'
  | 'MIDDLE_ROW'
  | 'BOTTOM_ROW'
  | 'FULL_HOUSE';

export type GameStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'LOBBY'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELLED';

export type FalseClaimPolicy = 'NONE' | 'COOLDOWN' | 'BOGEY' | 'STRIKES';

export interface Reward {
  id: string;
  type: RewardType;
  label: string;
  prizeText: string;
  enabled: boolean;
  winnersAllowed: number;
  winnersAwarded: number;
}

export interface Game {
  title: string;
  /** Host's name, denormalised onto the game so players can see it. */
  companyName: string | null;
  status: GameStatus;
  rewards: Reward[];
  settings: {
    falseClaimPolicy: FalseClaimPolicy;
    falseClaimParams: { cooldownSec: number; maxStrikes: number };
    boardVisibleToPlayers: boolean;
  };
}

export interface LiveState {
  status: GameStatus;
  lastNumber: number | null;
  calledCount: number;
  recent: number[];
  lastWin: { playerName: string; rewardLabel: string; atMs: number } | null;
}

/** Ticket as stored: 27 flat cells, because Firestore rejects nested arrays. */
export interface StoredTicket {
  id: string;
  cells: (number | null)[];
  numbers: number[];
}

export interface PlayerState {
  name: string;
  tickets: StoredTicket[];
  marks: number[];
  wonRewardIds: string[];
  blockedRewardIds: string[];
}

export const TICKET_ROWS = 3;
export const TICKET_COLS = 9;

/** Rebuilds the 3x9 grid from the flat stored form. */
export function ticketRows(ticket: StoredTicket): (number | null)[][] {
  const rows: (number | null)[][] = [];
  for (let row = 0; row < TICKET_ROWS; row++) {
    rows.push(ticket.cells.slice(row * TICKET_COLS, (row + 1) * TICKET_COLS));
  }
  return rows;
}

/* ------------------------------------------------------------------ joining */

export interface JoinResult {
  playerId: string;
  playerName: string;
  tookOverDevice: boolean;
}

/**
 * A browser has no stable device id, so one is generated and kept in
 * localStorage. It only needs to be stable per browser — it exists so the
 * server can tell "same person reopening the tab" from "second device".
 */
function deviceId(): string {
  const key = 'tambola.deviceId';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

export async function joinGame(gameId: string, token: string): Promise<JoinResult> {
  const redeem = callable<
    { gameId: string; token: string; deviceId: string },
    JoinResult & { customToken: string }
  >('redeemInvite');

  let data;
  try {
    ({ data } = await redeem({ gameId, token, deviceId: deviceId() }));
  } catch (error) {
    // The server carries the start time when it refuses an early join, so the
    // player can be told exactly when to come back.
    throw translateJoinError(error);
  }
  await signInWithCustomToken(auth, data.customToken);
  return {
    playerId: data.playerId,
    playerName: data.playerName,
    tookOverDevice: data.tookOverDevice,
  };
}

function translateJoinError(error: unknown): Error {
  const details = (error as { details?: { reason?: string; scheduledStartAt?: number } })?.details;
  if (details?.reason === 'JOIN_TOO_EARLY' && typeof details.scheduledStartAt === 'number') {
    const fmt = new Intl.DateTimeFormat('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
    const start = fmt.format(new Date(details.scheduledStartAt));
    const opens = fmt.format(new Date(details.scheduledStartAt - 30 * 60 * 1000));
    return new Error(
      `This game hasn't opened yet. It starts ${start}, and you can join from ${opens}.`,
    );
  }
  return error instanceof Error ? error : new Error("That invite link didn't work.");
}

/* ----------------------------------------------------------------- streams */

export function watchGame(gameId: string, onChange: (game: Game | null) => void): () => void {
  return onSnapshot(
    doc(db, 'games', gameId),
    (snap) => onChange(snap.exists() ? (snap.data() as Game) : null),
    () => onChange(null),
  );
}

export function watchLive(gameId: string, onChange: (live: LiveState | null) => void): () => void {
  return onSnapshot(
    doc(db, 'games', gameId, 'state', 'live'),
    (snap) => onChange(snap.exists() ? (snap.data() as LiveState) : null),
    () => onChange(null),
  );
}

export function watchPlayer(
  gameId: string,
  playerId: string,
  onChange: (player: PlayerState | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'games', gameId, 'players', playerId),
    (snap) => onChange(snap.exists() ? (snap.data() as PlayerState) : null),
    () => onChange(null),
  );
}

/**
 * The full board.
 *
 * Emits null when the organiser has kept it hidden: the read is refused by the
 * security rules, which arrives here as an error rather than an empty result.
 */
export function watchBoard(
  gameId: string,
  onChange: (called: number[] | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'games', gameId, 'state', 'board'),
    (snap) => onChange(snap.exists() ? ((snap.data()['calledNumbers'] as number[]) ?? []) : null),
    () => onChange(null),
  );
}

/* ----------------------------------------------------------------- actions */

export type ClaimResponse =
  | { valid: true; rank: number | null; gameCompleted: boolean }
  | { valid: false; reason: string };

export async function submitClaim(
  gameId: string,
  rewardId: string,
  ticketId: string,
  claimedAtIndex: number,
): Promise<ClaimResponse> {
  const claim = callable<
    { gameId: string; rewardId: string; ticketId: string; claimedAtIndex: number },
    ClaimResponse
  >('submitClaim');
  const { data } = await claim({ gameId, rewardId, ticketId, claimedAtIndex });
  return data;
}

/** Marks are cosmetic; a failed sync is not worth interrupting play for. */
export async function syncMarks(
  gameId: string,
  playerId: string,
  marks: number[],
): Promise<void> {
  try {
    await updateDoc(doc(db, 'games', gameId, 'players', playerId), {
      marks,
      marksUpdatedAt: Date.now(),
    });
  } catch {
    // Deliberately swallowed — see above.
  }
}
